import { useField } from "@formiz/core";
import { UseFieldProps } from "@formiz/core/dist/types/field.types";
import { Icon, IconSize, IconVariants, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { cloneElement, isValidElement, JSX, useState } from "react";

import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";

interface IFormFieldProps extends UseFieldProps {
  as: "input" | "textarea" | "checkbox" | "custom";
  children: JSX.Element;
  showError?: boolean;
  toLowerCase?: boolean;
  label?: string;
  required?: boolean;
  description?: string;
  errorMessage?: string;
  countLabel?: string;
  "data-field-name"?: string;
  fieldInfoLink?: string;
  disabled?: boolean;
  dataTestId?: string;
}

export const FormField = (props: IFormFieldProps) => {
  const {
    as,
    children,
    showError,
    toLowerCase,
    label,
    required,
    description,
    "data-field-name": dataFieldName,
    countLabel,
    fieldInfoLink,
    dataTestId = "",
    ...rest
  } = props;

  const { value, setValue, errorMessage, isValid, isPristine, valueDebounced } = useField(rest);
  const [isFocused, setIsFocused] = useState(false);

  const isValueExist = () => {
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return value;
  };

  const isValueChanging = value !== valueDebounced;

  const isError = !!errorMessage && !isValid && (showError || (!isFocused && !isValueChanging && (!isPristine || isValueExist())));

  const childTypeName = children?.type?.displayName || children?.type?.name;

  const isInputElements = ["input", "textarea"].includes(childTypeName?.toLowerCase?.() || as);
  const isCheckbox = ["checkbox"].includes(childTypeName?.toLowerCase?.() || as);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement> | string | boolean) => {
    const newValue = typeof e !== "object" ? e : typeof e?.target?.checked === "boolean" ? e.target.checked : e?.target?.value;
    setValue(toLowerCase && typeof newValue !== "boolean" ? newValue?.toLowerCase() : newValue);
  };

  const childProps = (() => {
    if (isCheckbox) {
      return {
        ...children.props,
        checked: value,
        onChange: handleChange,
        name: rest.name ?? children.props?.name,
        disabled: props.disabled ?? children.props?.disabled,
      };
    }

    if (isInputElements) {
      return {
        value: value ?? "",
        onChange: handleChange,
        error: isError,
        errorMessage: isError ? errorMessage : "",
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        ...rest,
        helperText: description || children.props?.helperText,
      };
    }

    return {
      value,
      onChange: handleChange,
      ...rest,
    };
  })();

  const shouldShowError = !isInputElements && isError && errorMessage;
  const displayText = shouldShowError ? errorMessage : description;
  const textColor = shouldShowError ? "text-red-9" : "text-gray-11";

  return (
    <div className="flex flex-col gap-1.5" data-field-name={dataFieldName} data-testid={dataTestId}>
      {label && (
        <div className="flex gap-1 items-center">
          <Text variant={TextVariants.LABEL_LARGE_MEDIUM}>{label}</Text>
          {required && <Text className="text-red-10">*</Text>}
          {fieldInfoLink && (
            <LocalizedLink target="_blank" to={fieldInfoLink}>
              <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="text-gray-12" />
            </LocalizedLink>
          )}
        </div>
      )}
      {isValidElement(children) ? cloneElement(children, childProps) : children}
      {(displayText || countLabel) && (
        <div className="flex justify-between">
          {(errorMessage || description) && (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className={textColor}>
              {displayText}
            </Text>
          )}
          {countLabel && (
            <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="ml-auto max-w-max text-gray-12">
              {countLabel}
            </Text>
          )}
        </div>
      )}
    </div>
  );
};
